import type { Skill, SoundType } from '../../types';
import { allCards } from '../db';
import { studyCodes } from '../ignored';
import { studySession } from '../fsrs';
import { narrow } from './focus';
import { buildSoundQuestion, soundPool, type SoundQuestion } from './sound';
import { buildNameQuestion, type NameQuestion } from './names';
import { buildPhotoQuestion, photoPool, type PhotoQuestion } from './photo';
import { DEFAULT_ROUND_LEN, newBirdShare } from './settings';

// The Field Test: one round that draws from all three skills.
//
// Every other round in the app is single-skill, and the daily challenge only
// rotates the skills BETWEEN days — so nothing ever asked the question a real
// encounter asks, which is "what is that" with no warning about which sense is
// about to be used. A field test interleaves them: a recording, then a
// photograph, then a binomial, in an order the learner cannot pre-load for.
//
// It is not a fourth skill and it creates no fourth card. Each question grades
// the ordinary `code:skill` card of whichever skill it came from, so a field
// test is credit toward Sound ID, Name Match and Photo ID at once, and the
// mastery rings move exactly as they would have.

/** One question, tagged with the skill it is testing. */
export type FieldQuestion =
  | { skill: 'sound'; question: SoundQuestion }
  | { skill: 'names'; question: NameQuestion }
  | { skill: 'photo'; question: PhotoQuestion };

/** The skills a field test draws from, in the order it deals its first pass. */
export const FIELD_SKILLS: readonly Skill[] = ['sound', 'names', 'photo'];

/**
 * The species a skill can ask about right now — the same narrowing each mode's
 * own session builder does, for the same reason: a species queued without the
 * media its question needs comes back null and is silently dropped, so a round
 * of ten is quietly shorter.
 */
function poolFor(
  skill: Skill,
  voices?: readonly SoundType[],
  focus?: readonly string[],
): string[] {
  // Two narrowings, and they answer different questions. The first is what this
  // skill CAN ask about — the birds with a recording, or with a photograph. The
  // second is what this round WILL ask about, when it was started from a
  // filtered list rather than from the study grid.
  if (skill === 'sound') return narrow(soundPool(voices), focus);
  if (skill === 'photo') return narrow(photoPool(), focus);
  return narrow(studyCodes(), focus);
}

/**
 * Build one question of `skill` for a known species.
 *
 * Both name-bearing skills run in the "you have the bird, produce the name"
 * direction — Photo ID as Photo → Name, Name Match as common → scientific —
 * because that is the direction a field encounter runs in. Name → Photo would
 * be the field test handing over the answer and asking for a lineup.
 */
function buildFieldQuestion(
  skill: Skill,
  code: string,
  rnd: () => number,
  voices?: readonly SoundType[],
): FieldQuestion | null {
  if (skill === 'sound') {
    const question = buildSoundQuestion(code, rnd, voices);
    return question ? { skill, question } : null;
  }
  if (skill === 'photo') {
    const question = buildPhotoQuestion(code, 'toName', rnd);
    return question ? { skill, question } : null;
  }
  const question = buildNameQuestion(code, 'toSci', rnd);
  return question ? { skill, question } : null;
}

/** Fisher-Yates on a copy, so a caller's list is never reordered under it. */
function shuffled<T>(list: readonly T[], rnd: () => number): T[] {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export interface FieldOptions {
  /**
   * Which skills are in the mix. Defaults to all three; the view drops Photo ID
   * when the media pack has not been downloaded, since every photo question
   * would otherwise be a placeholder.
   */
  skills?: readonly Skill[];
  /** What a sound question may play, honoured exactly as Sound ID honours it. */
  voices?: readonly SoundType[];
  /**
   * The birds this round is confined to — the Library's filtered list, or one
   * family. See core/quiz/focus.ts. Undefined is an ordinary round.
   */
  focus?: readonly string[];
}

/**
 * A field test: `len` questions dealt evenly across the skills in play, each one
 * off that skill's own FSRS queue.
 *
 * Dealt round-robin rather than by taking one queue and splitting it, because
 * the three skills are scheduled independently — the bird most overdue for a
 * sound is rarely the one most overdue for a photo, and a round built off any
 * single queue would be asking two of the three skills the wrong questions.
 * Each pass through the skills is shuffled, so the round is not a metronome the
 * learner can count: knowing a photo is next is half of being ready for it.
 *
 * A species is never asked twice in a pass even if two skills both want it.
 * Hearing a bird and then immediately being shown it is a hint, not a test, and
 * the queues do overlap — a species never met is at the front of all three. Once
 * every queue is spent the round starts another pass, which is what turns a
 * focus of one bird into a drill rather than a round of one question; no bird is
 * ever asked the same WAY twice, so the round is at most one question per
 * species per skill.
 */
export async function buildFieldSession(
  now: Date = new Date(),
  rnd: () => number = Math.random,
  len: number = DEFAULT_ROUND_LEN,
  opts: FieldOptions = {},
): Promise<FieldQuestion[]> {
  const wanted = opts.skills ?? FIELD_SKILLS;
  const pools = new Map<Skill, string[]>();
  for (const skill of wanted) {
    const pool = poolFor(skill, opts.voices, opts.focus);
    if (pool.length) pools.set(skill, pool);
  }
  if (!pools.size || len < 1) return [];

  // Each skill queues a full round's worth rather than its share of one: the
  // dedupe below and the odd unbuildable question both consume candidates, and
  // a queue that runs dry ends that skill's part of the round early.
  //
  // One new-bird share across all three, read once: the Field Test has a single
  // setup screen and asks for a single round, so its three queues are three
  // parts of one decision rather than three separate ones.
  const share = await newBirdShare('field');
  const queues = new Map<Skill, string[]>();
  await Promise.all(
    [...pools].map(async ([skill, pool]) => {
      queues.set(skill, await studySession(skill, pool, now, len, share));
    }),
  );

  const at = new Map<Skill, number>([...queues.keys()].map((s) => [s, 0]));
  // Round-wide: a bird is never asked the SAME way twice, however long the round
  // or however small the pool.
  const asked = new Set<string>();
  // This pass only: a bird is never asked twice running, in any skill.
  let used = new Set<string>();
  const out: FieldQuestion[] = [];
  let restarted = false;

  while (out.length < len) {
    let dealt = false;
    for (const skill of shuffled([...queues.keys()], rnd)) {
      if (out.length >= len) break;
      const queue = queues.get(skill)!;
      // Walk this skill's queue to its next usable species: one not already in
      // this pass, not already asked this way, and whose question this skill can
      // actually build.
      let i = at.get(skill)!;
      let q: FieldQuestion | null = null;
      while (i < queue.length && !q) {
        const code = queue[i++];
        if (used.has(code) || asked.has(`${code}:${skill}`)) continue;
        q = buildFieldQuestion(skill, code, rnd, opts.voices);
      }
      at.set(skill, i);
      if (!q) continue;
      used.add(q.question.target.code);
      asked.add(`${q.question.target.code}:${skill}`);
      out.push(q);
      dealt = true;
      restarted = false;
    }
    if (dealt) continue;
    // Every queue is spent. On any ordinary round that is the end of it — two
    // hundred species never exhaust the first pass — but a round focused on ONE
    // bird runs dry after a single question, and a drill of one question is not
    // a drill. So the queues rewind and the round takes another pass, this time
    // allowed the birds it has already used in a different skill: one bird by
    // sound, then by sight, then by name.
    if (restarted) break; // a full rewind that dealt nothing: there is no more
    restarted = true;
    for (const skill of queues.keys()) at.set(skill, 0);
    used = new Set();
  }
  return out;
}

/** How each skill fared in a finished round — the field test's own scorecard. */
export interface FieldTally {
  skill: Skill;
  asked: number;
  right: number;
}

/**
 * Score a finished round per skill, in the order the skills were dealt.
 *
 * A single "7/10" is the one number a mixed round cannot be summed up by: it
 * hides that the three sounds were all wrong and the four names all right,
 * which is the only thing the round knows that a single-skill round does not.
 */
export function tally(session: FieldQuestion[], correct: readonly boolean[]): FieldTally[] {
  const bySkill = new Map<Skill, FieldTally>();
  session.forEach((q, i) => {
    const row = bySkill.get(q.skill) ?? { skill: q.skill, asked: 0, right: 0 };
    row.asked += 1;
    if (correct[i]) row.right += 1;
    bySkill.set(q.skill, row);
  });
  return FIELD_SKILLS.filter((s) => bySkill.has(s)).map((s) => bySkill.get(s)!);
}

// ---- The review round -------------------------------------------------------
//
// The same mixed deal, fed from the other end. A field test asks for a round of
// a chosen length and lets FSRS pick what goes in it; a review round starts from
// what is actually DUE and asks for exactly that, mixed the same way.
//
// It replaced the daily challenge, which was one question a day off a
// three-skill rotation — a habit-former that had nothing to do with what the
// scheduler wanted back. What a spaced-repetition app owes its learner every day
// is the cards that have come around, which is this.

/**
 * The most a review round will ask in one sitting.
 *
 * The queue can be any size — a fortnight away and every card in the region is
 * due — and a round that took an hour would simply not get started. Twenty is
 * about ten minutes, the rest keeps, and clearing twenty is the fastest way to
 * shorten the queue anyway.
 */
export const REVIEW_CAP = 20;

/**
 * The fewest due cards worth building a round out of.
 *
 * Below this the home panel says nothing and the cards simply keep. Two cards
 * cost a tap, a round, a scorecard and a trip back to Home to clear, which is
 * more ceremony than two questions are worth — and a panel that reappears every
 * afternoon asking for two reads as a nag rather than as the day's work. They
 * come back into view the moment a third joins them, which on any real deck is
 * the same day or the next one.
 */
export const REVIEW_FLOOR = 3;

/** Rough seconds a question costs, for the time estimate on the home screen. */
export const SECONDS_PER_QUESTION = 10;

export interface ReviewOptions extends FieldOptions {
  /** Round cap; defaults to REVIEW_CAP. */
  cap?: number;
}

export interface DueQueue {
  /** Cards due per skill, over the whole queue rather than the next round. */
  bySkill: Record<Skill, number>;
  /** Everything due right now. */
  total: number;
  /** How many of them the next round will actually ask. */
  round: number;
  /** Whether the queue is longer than one round. */
  capped: boolean;
  /** Rough minutes for the round about to be dealt, at least 1. */
  minutes: number;
}

interface DueCard {
  skill: Skill;
  code: string;
  /** Due time in ms, so the sort is a number compare. */
  at: number;
}

/**
 * Every card that has come around, most overdue first.
 *
 * A card exists only once its skill has actually asked about the bird, so
 * "cards that are due" is already "birds you have heard, seen or named" — no
 * separate met-check is needed, and a bird you have never met can never be in
 * here however long it has sat in the region.
 *
 * Filtered to the selected packs and to the species each skill can build a
 * question for, for the reason `poolFor` gives: a card queued without its media
 * is a question that comes back null and quietly shortens the round.
 */
async function dueCards(now: Date, opts: ReviewOptions): Promise<DueCard[]> {
  const active = new Set(studyCodes());
  const pools = new Map<Skill, Set<string>>();
  for (const skill of opts.skills ?? FIELD_SKILLS) {
    // No focus here, deliberately, though `ReviewOptions` inherits the field.
    // A review round is what you owe; narrowing it to nine sparrows would not
    // make the rest of the debt go away, it would only hide it.
    pools.set(skill, new Set(poolFor(skill, opts.voices)));
  }

  const cutoff = now.getTime();
  const out: DueCard[] = [];
  for (const rec of await allCards()) {
    const pool = pools.get(rec.skill);
    if (!pool || !active.has(rec.code) || !pool.has(rec.code)) continue;
    const at = rec.due.getTime();
    if (at > cutoff) continue;
    out.push({ skill: rec.skill, code: rec.code, at });
  }
  return out.sort((a, b) => a.at - b.at);
}

/**
 * What is waiting: the counts the home screen puts under "Due today", and the
 * size of the round that would clear them.
 *
 * Counts the whole queue, not the capped round — the panel is reporting the
 * state of the deck, and a number that silently stopped at twenty would tell a
 * learner three weeks behind that they were one round from clear.
 */
export async function dueQueue(
  now: Date = new Date(),
  opts: ReviewOptions = {},
): Promise<DueQueue> {
  const cards = await dueCards(now, opts);
  const bySkill: Record<Skill, number> = { sound: 0, names: 0, photo: 0 };
  for (const c of cards) bySkill[c.skill] += 1;
  const cap = opts.cap ?? REVIEW_CAP;
  const round = Math.min(cards.length, cap);
  return {
    bySkill,
    total: cards.length,
    round,
    capped: cards.length > cap,
    minutes: Math.max(1, Math.round((round * SECONDS_PER_QUESTION) / 60)),
  };
}

/**
 * The review round: the most overdue cards, dealt mixed.
 *
 * Overdue order decides WHICH cards are in the round; it deliberately does not
 * decide the order they are asked in. Straight overdue order comes out as a run
 * of eleven sounds and then a run of photos, because the three skills are
 * scheduled independently and a fortnight's absence expires them in blocks —
 * and a run of eleven sounds is a Sound ID round, not a review of your birds.
 *
 * A species due in two skills stays in twice: both cards are work the scheduler
 * asked for, and dropping one silently leaves the queue un-clearable. They are
 * kept apart instead, so the second is a question rather than a reminder.
 */
export async function buildReviewSession(
  now: Date = new Date(),
  rnd: () => number = Math.random,
  opts: ReviewOptions = {},
): Promise<FieldQuestion[]> {
  const cap = opts.cap ?? REVIEW_CAP;
  if (cap < 1) return [];
  const queues = new Map<Skill, FieldQuestion[]>();
  let built = 0;

  // Walks past the cap when a card can't be built into a question, so a round
  // is short only when the QUEUE is short, not when one recording is missing.
  for (const c of await dueCards(now, opts)) {
    if (built >= cap) break;
    const q = buildFieldQuestion(c.skill, c.code, rnd, opts.voices);
    if (!q) continue;
    const list = queues.get(c.skill) ?? [];
    list.push(q);
    queues.set(c.skill, list);
    built += 1;
  }

  return spaceOut(interleave(queues, rnd));
}

/** Deal one question per skill per pass, in a shuffled skill order each time. */
function interleave(
  queues: Map<Skill, FieldQuestion[]>,
  rnd: () => number,
): FieldQuestion[] {
  const at = new Map<Skill, number>([...queues.keys()].map((s) => [s, 0]));
  const out: FieldQuestion[] = [];
  let dealt = true;
  while (dealt) {
    dealt = false;
    for (const skill of shuffled([...queues.keys()], rnd)) {
      const i = at.get(skill)!;
      const queue = queues.get(skill)!;
      if (i >= queue.length) continue;
      out.push(queue[i]);
      at.set(skill, i + 1);
      dealt = true;
    }
  }
  return out;
}

/**
 * Push any bird that landed next to itself further down the round.
 *
 * One swap per position, and none at all if there is nothing to swap with: the
 * back-to-back pair at the end of a round that is entirely one species is a
 * true fact about the queue, not a bug to loop over.
 */
function spaceOut(round: FieldQuestion[]): FieldQuestion[] {
  const codeAt = (i: number): string => round[i].question.target.code;
  for (let i = 1; i < round.length; i++) {
    if (codeAt(i) !== codeAt(i - 1)) continue;
    for (let j = i + 1; j < round.length; j++) {
      if (codeAt(j) === codeAt(i - 1)) continue;
      [round[i], round[j]] = [round[j], round[i]];
      break;
    }
  }
  return round;
}
